const normalizeRole = role => {
  const normalized = String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
  if (['administrator', 'tenant administrator'].includes(normalized)) return 'admin';
  return normalized;
};

const isCentralTenantAdmin = user =>
  Boolean(user?.centralTenantAdmin) || normalizeRole(user?.userRole) === 'admin';

const BILLING_FULL_ACCESS = [
  '/dashboard',
  '/RCM Insights',
  '/encounters',
  '/EncounterDetail',
  '/simulation',
  '/claim-rejection',
  '/billing/eras',
];

const BILLING_ENCOUNTERS_AND_CLAIMS = [
  '/encounters',
  '/EncounterDetail',
  '/claim-rejection',
];

const SHARED_APPLICATION_ROLES = new Set([
  'admin',
  'director',
  'case manager',
  'clinical director',
  'clinical supervisor',
]);

const ROLE_ACCESS = {
  admin: [...BILLING_FULL_ACCESS, '/Director'],
  director: [...BILLING_FULL_ACCESS],
  'case manager': [...BILLING_ENCOUNTERS_AND_CLAIMS],
  'clinical director': [...BILLING_ENCOUNTERS_AND_CLAIMS],
  'clinical supervisor': [...BILLING_ENCOUNTERS_AND_CLAIMS],
};

const DEFAULT_PATH_BY_ROLE = {
  admin: '/dashboard',
  director: '/dashboard',
  'case manager': '/encounters',
  'clinical director': '/encounters',
  'clinical supervisor': '/encounters',
};

const BILLING_PATHS = new Set(BILLING_FULL_ACCESS);

const hasPath = (paths, path) =>
  paths.some(allowedPath => path === allowedPath || path.startsWith(`${allowedPath}/`));

const unique = paths => Array.from(new Set(paths));

const getEffectiveAccess = user => {
  const role = normalizeRole(user?.userRole);
  const applications = user?.tenantApplications;
  const roleAccess = ROLE_ACCESS[role] || [];

  // Preserve the role-only fallback only while restoring legacy sessions.
  // Current authenticated sessions always include tenantApplications.
  if (!applications) return roleAccess;

  const access = roleAccess.filter(path => {
    if (path === '/Director') return role === 'admin';
    if (BILLING_PATHS.has(path)) return Boolean(applications.insights);
    return false;
  });

  if (SHARED_APPLICATION_ROLES.has(role)) {
    if (applications.providerCredential) access.push('/provider-credential');
    if (applications.medicalAssist) access.push('/medical-assist');
    if (applications.priorAuth) access.push('/prior-authorization');
  }

  // The server projects tenant-enabled ABASphere and RightMAT access for a
  // central tenant administrator. Non-admin users reach these paths only
  // after an application-specific assignment. Keep the server projection
  // authoritative so a disabled tenant application never appears here.
  if (applications.aba) access.push('/aba');
  if (applications.methadone) access.push('/methadone');
  if (applications.rightEmr) access.push('/rightemr');

  return unique(access);
};

const canAccessPath = (user, path) => {
  if (user?.isPlatformAdmin && user?.portal === 'platform') {
    return path === '/platform-admin' || path.startsWith('/platform-admin/');
  }
  return hasPath(getEffectiveAccess(user), path);
};

const getDefaultPath = user => {
  if (user?.isPlatformAdmin && user?.portal === 'platform') return '/platform-admin';
  const role = normalizeRole(user?.userRole);
  const preferredPath = DEFAULT_PATH_BY_ROLE[role];
  if (preferredPath && canAccessPath(user, preferredPath)) return preferredPath;
  return getEffectiveAccess(user)[0] || '/access-unavailable';
};

export { canAccessPath, getDefaultPath, isCentralTenantAdmin, normalizeRole };
